/**
 * OCR Servisi — Native cihaz içi OCR
 *
 * @capacitor-community/image-to-text kullanır:
 * - iOS: Apple Vision Framework
 * - Android: Google ML Kit
 * - Harici AI/API yoktur, fotoğraf backend'e gönderilmez.
 *
 * Bu servis yalnızca metin çıkarır ve güvenli kural bazlı parser döndürür.
 */

import { Capacitor } from '@capacitor/core';
import { parseShoppingText } from './shoppingOcrService';

/* ─── Fotoğraf Seç ────────────────────────────────────────────────────────── */

/**
 * HTML file input ile fotoğraf seç veya çek.
 * iOS Capacitor WebView'da native kamera/galeri açar.
 * @param {'kamera'|'galeri'} kaynak
 * @returns {Promise<{base64: string, dataUrl: string}>}
 */
export function fotografSec(kaynak = 'kamera') {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (kaynak === 'kamera') input.capture = 'environment';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);

    let settled = false;

    const cleanup = () => {
      try { document.body.removeChild(input); } catch {}
    };

    const finish = (fn) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const timeout = setTimeout(() => {
      finish(() => reject(new Error('TIMEOUT')));
    }, 120_000);

    input.onchange = (e) => {
      clearTimeout(timeout);
      const file = e.target.files?.[0];
      if (!file) {
        finish(() => reject(new Error('CANCELLED')));
        return;
      }

      if (!String(file.type || '').startsWith('image/')) {
        finish(() => reject(new Error('INVALID_IMAGE')));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || '');
        const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
        finish(() => resolve({ base64, dataUrl }));
      };
      reader.onerror = () => {
        finish(() => reject(reader.error || new Error('READ_FAILED')));
      };
      reader.readAsDataURL(file);
    };

    const onFocus = () => {
      window.removeEventListener('focus', onFocus);
      setTimeout(() => {
        if (!settled && document.body.contains(input) && !input.files?.length) {
          clearTimeout(timeout);
          finish(() => reject(new Error('CANCELLED')));
        }
      }, 700);
    };
    window.addEventListener('focus', onFocus);

    input.click();
  });
}



/* ─── Görsel OCR İyileştirme ──────────────────────────────────────────────── */

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('IMAGE_LOAD_FAILED'));
    img.src = dataUrl;
  });
}

function canvasToDataUrl(canvas, quality = 0.92) {
  try {
    return canvas.toDataURL('image/jpeg', quality);
  } catch {
    return '';
  }
}

async function enhanceImageForOcr(imageInput, mode = 'generic') {
  const raw = String(imageInput || '');
  if (!raw.startsWith('data:image/')) return raw;
  if (typeof document === 'undefined') return raw;

  try {
    const img = await loadImage(raw);
    const maxSide = mode === 'shopping' ? 1800 : 2200;
    const scale = Math.min(1, maxSide / Math.max(img.width || 1, img.height || 1));
    const width = Math.max(1, Math.round((img.width || 1) * scale));
    const height = Math.max(1, Math.round((img.height || 1) * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return raw;

    ctx.drawImage(img, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // El yazısı için biraz daha sert kontrast, fiş için daha doğal kontrast.
    const contrast = mode === 'shopping' ? 1.32 : 1.18;
    const brightness = mode === 'shopping' ? 8 : 4;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      let gray = (0.299 * r) + (0.587 * g) + (0.114 * b);
      gray = ((gray - 128) * contrast) + 128 + brightness;
      gray = Math.max(0, Math.min(255, gray));
      // Hafif arka plan temizliği: kağıt beyazı beyaza, koyu mürekkep koyuya yaklaşır.
      if (mode === 'shopping') {
        if (gray > 212) gray = 255;
        else if (gray < 92) gray = Math.max(0, gray - 20);
      }
      data[i] = data[i + 1] = data[i + 2] = gray;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvasToDataUrl(canvas) || raw;
  } catch {
    return raw;
  }
}

/* ─── OCR ─────────────────────────────────────────────────────────────────── */

function readPoint(item, primary, secondary) {
  const value = item?.[primary] || item?.[secondary];
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    return [value.x ?? value[0] ?? 0, value.y ?? value[1] ?? 0];
  }
  return [0, 0];
}

function normalizeDetectionItem(item) {
  const text = String(item?.text || item?.value || item?.rawValue || '').trim();
  const topLeft = readPoint(item, 'topLeft', 'top_left');
  const bottomLeft = readPoint(item, 'bottomLeft', 'bottom_left');
  const topRight = readPoint(item, 'topRight', 'top_right');
  const bottomRight = readPoint(item, 'bottomRight', 'bottom_right');
  const xs = [topLeft[0], bottomLeft[0], topRight[0], bottomRight[0]].map(Number).filter(Number.isFinite);
  const ys = [topLeft[1], bottomLeft[1], topRight[1], bottomRight[1]].map(Number).filter(Number.isFinite);
  const x = xs.length ? Math.min(...xs) : 0;
  const y = ys.length ? Math.min(...ys) : 0;
  const height = ys.length ? Math.max(...ys) - Math.min(...ys) : 0;
  return { text, x, y, height };
}

function normalizeDetections(detections = []) {
  const safe = Array.isArray(detections) ? detections : [];
  const items = safe.map(normalizeDetectionItem).filter((item) => item.text);
  if (!items.length) return [];

  const hasGeometry = items.some((item) => item.x || item.y || item.height);
  if (!hasGeometry) return items.map((item) => item.text);

  const sorted = [...items].sort((a, b) => {
    const yDiff = a.y - b.y;
    if (Math.abs(yDiff) > 0.003) return yDiff;
    return a.x - b.x;
  });

  const avgHeight = sorted.reduce((sum, item) => sum + Math.max(0, item.height || 0), 0) / sorted.length;
  const yThreshold = Math.max(avgHeight * 0.75, 0.014);
  const rows = [];

  for (const item of sorted) {
    const row = rows.find((r) => Math.abs(r.y - item.y) <= yThreshold);
    if (row) {
      row.items.push(item);
      row.y = (row.y * (row.items.length - 1) + item.y) / row.items.length;
    } else {
      rows.push({ y: item.y, items: [item] });
    }
  }

  return rows
    .sort((a, b) => a.y - b.y)
    .map((row) => row.items
      .sort((a, b) => a.x - b.x)
      .map((item) => item.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim())
    .filter(Boolean);
}

/**
 * Native OCR ile görseldeki metni tanı.
 * @param {string} imageInput data:image/... ya da base64 string
 * @param {Function} [onProgress] kullanıcıya basit ilerleme göstermek için callback
 * @returns {Promise<string>} Ham metin
 */
export async function metinTani(imageInput, onProgress, options = {}) {
  if (!imageInput) return '';

  if (!Capacitor.isNativePlatform()) {
    throw new Error('NATIVE_OCR_REQUIRED');
  }

  try {
    onProgress?.(10);
    const { Ocr } = await import('@capacitor-community/image-to-text');
    const preparedInput = await enhanceImageForOcr(imageInput, options?.mode || 'generic');
    const base64 = String(preparedInput).includes(',')
      ? String(preparedInput).split(',')[1]
      : String(preparedInput);

    onProgress?.(35);
    const data = await Ocr.detectText({ base64 });
    onProgress?.(85);

    const lines = normalizeDetections(data?.textDetections);
    onProgress?.(100);
    return lines.join('\n');
  } catch (err) {
    if (err?.message === 'NATIVE_OCR_REQUIRED') throw err;
    throw new Error('OCR_FAILED');
  }
}

/* ─── Alışveriş Listesi Parser ────────────────────────────────────────────── */

/**
 * El yazısı / baskılı alışveriş listesinden ürün + miktar + birim + kategori çıkar.
 * @param {string} rawText
 * @param {{availableCategories?: string[], availableUnits?: string[]}} [options]
 * @returns {{ad:string,miktar:string,birim:string,kategori:string}[]}
 */
export function alisverisListesiParse(rawText, options = {}) {
  return parseShoppingText(rawText, options);
}

/* ─── Market Fişi Parser ──────────────────────────────────────────────────── */

const FIS_SKIP = [
  /\b(ara\s*)?toplam\b/i, /\bara\s*top\b/i, /\bmal\s*\/\s*hizmet\s*toplam/i, /\bödenecek\b/i, /\bodenecek\b/i,
  /\bkdv\b/i, /\btopkdv\b/i, /\btax\b/i, /\btotal\b/i, /\bsubtotal\b/i,
  /\bnakit\b/i, /\bcash\b/i, /\bkredi\s*kart/i, /\bkart\b/i, /\bcard\b/i,
  /\bpara\s*üstü\b/i, /\bchange\b/i, /\bpuan\b/i, /\bbonus\b/i,
  /\bfiş\s*(no|nu)\b/i, /\bfis\s*(no|nu)\b/i, /\breceipt\b/i, /\bkasiyer\b/i, /\bcashier\b/i,
  /\btarih\b/i, /\bdate\b/i, /\bsaat\b/i, /\btime\b/i,
  /\btelefon\b/i, /\btel\b/i, /\bphone\b/i, /\badres\b/i, /\baddress\b/i,
  /\bteşekkür\b/i, /\btesekkur\b/i, /\bthank\b/i, /\bgüle güle\b/i, /\byine\s*bekleriz\b/i,
  /\bindiri[mn]/i, /\bind\./i, /\bdiscount\b/i, /\bkampanya\b/i, /\bürün\s*iptal\b/i, /\burun\s*iptal\b/i,
  /\bödeme\b/i, /\bodeme\b/i, /\bpayment\b/i, /\bvergi\b/i, /\bbakiye\b/i,
  /\btek\s*pos\b/i, /\bortak\s*pos\b/i, /\bsatis\b/i, /\bsatış\b/i, /\bonay\b/i, /\bprovizyon\b/i, /\bterminal\b/i, /\bref\.?\s*no\b/i,
  /\bziraat\b/i, /\byapi\s*kredi\b/i, /\byapikredi\b/i, /\bgaranti\b/i, /\bakbank\b/i, /\bis\s*bank/i, /\biş\s*bank/i, /\bbanka\b/i, /\bbank\b/i, /\bhalk\s*bank/i,
  /\bbelge\b/i, /\bettn\b/i, /\bmersis\b/i, /\bwww\b/i, /@/, /\bmerkez\s*adres/i,
  /\bmağaza\b/i, /\bmagaza\b/i, /\bmağazacılık\b/i, /\bmagazacilik\b/i,
  /\bvd\b/i, /\bv\.d\b/i, /\bvergi\s*dairesi\b/i, /\bmükellef\b/i, /\bmukellef\b/i,
];

const PRODUCT_HINTS = /(sut|süt|ekmek|yumurta|peynir|yogurt|yoğurt|ayran|su|domates|salatalik|salatalık|biber|elma|muz|meyve|sebze|et|tavuk|balik|balık|kiyma|kıyma|pirinc|pirinç|bulgur|makarna|un|seker|şeker|cay|çay|kahve|deterjan|sabun|sampuan|şampuan|bez|mendil|poşet|poset|bisküvi|biskuvi|çerez|cerez|kaju|badem|fıstık|fistik|finish|duru|ülker|ulker|eti|namet|pınar|pinar|migros|sok|şok|a101|bim|carrefour|market)/i;
const FIS_AMOUNT_SOURCE = String.raw`\d{1,6}(?:\s*[.,]\s*\d{2})`;
const FIS_PRICE_TOKEN_RE = new RegExp(String.raw`(?:^|\s)([*₺]?\s*${FIS_AMOUNT_SOURCE}\s*-?\s*(?:₺|TL|TRY)?)(?:\s*)$`, 'i');
const FIS_PRICE_ONLY_RE = new RegExp(String.raw`^\s*[*₺]?\s*(${FIS_AMOUNT_SOURCE})\s*(?:₺|TL|TRY)?\s*$`, 'i');
const FIS_DATE_RE = /\b\d{1,2}[./-]\d{1,2}[./-]\d{2,4}\b/;
const FIS_TIME_RE = /\b\d{1,2}:\d{2}(?::\d{2})?\b/;
const KDV_TOKEN_RE = /(?:^|\s)%\s*\d{1,2}\s*[.,]?\s*(?:$|\s)/g;
const BARCODE_RE = /^\s*\d{7,14}\s*$/;
const QUANTITY_ONLY_RE = /^\s*(?:\d+[.,]\d+\s*)?(?:kg|g|gr|lt|l|ml)?\s*x\s*\d{1,6}(?:[.,]\d{2})(?:\s*tl\s*\/?\s*(?:kg|ad|lt|l))?\s*$/i;

function parseReceiptPrice(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 && value <= 99999 ? value : null;
  }
  const raw = String(value || '').trim();
  // İndirim/iade satırları çoğu fişte fiyatın sonunda eksi ile gelir: *3,23-
  if (!raw || /-\s*(?:₺|TL|TRY)?\s*$/i.test(raw)) return null;
  const re = new RegExp(FIS_AMOUNT_SOURCE, 'g');
  const matches = [...raw.matchAll(re)].map((m) => m[0]);
  if (!matches.length) return null;
  const token = matches[matches.length - 1].replace(/\s/g, '').replace(',', '.');
  const price = parseFloat(token);
  // 0,25 gibi poşet fiyatlarını koru; ama makul dışı rakamları at.
  if (Number.isNaN(price) || price <= 0 || price > 99999) return null;
  return price;
}

function formatReceiptPriceInput(price) {
  const n = Number(price);
  if (!Number.isFinite(n)) return '0,00';
  return n.toFixed(2).replace('.', ',');
}

function cleanReceiptName(value = '') {
  return String(value)
    .replace(/[|;]/g, ' ')
    .replace(/[*₺]+/g, ' ')
    .replace(KDV_TOKEN_RE, ' ')
    .replace(/%\s*\d{1,2}\s*[.,]?/g, ' ')
    .replace(/^[-–—•*]+\s*/, '')
    // Sadece liste numarası gibi görünen başları temizle: "1. Ürün", "2) Ürün".
    // "150 G ANTEPFISTIK" gibi ürün miktarlarını silme.
    .replace(/^\d{1,2}\s*[.)-]\s+/, '')
    .replace(/\b\d{1,4}\s*[.,]\s*\d{2}\s*tl\s*\/\s*(?:kg|ad|lt|l)\b/gi, ' ')
    // Satır sonundaki fiyat kalıntısını temizle ama ürün içindeki 150G / 2 KG bilgilerini koru.
    .replace(/\s+\d{1,6}\s*[.,]\s*\d{2}\s*$/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeReceiptLine(line = '') {
  return String(line)
    .replace(/[“”]/g, '"')
    .replace(/[×]/g, 'x')
    .replace(/[₺]/g, '*')
    .replace(/\s+/g, ' ')
    .trim();
}

function shouldSkipWholeLine(line = '') {
  const clean = normalizeReceiptLine(line);
  if (!clean || clean.length < 2) return true;
  if (BARCODE_RE.test(clean)) return true;
  if (FIS_DATE_RE.test(clean) || FIS_TIME_RE.test(clean)) return true;
  if (QUANTITY_ONLY_RE.test(clean.toLocaleLowerCase('tr-TR'))) return true;
  if (/^[-–—_\s]+$/.test(clean)) return true;
  if (/^\s*%\s*\d{1,2}\s*$/.test(clean)) return true;
  if (new RegExp(String.raw`^\s*(?:\d+\s*)?x\s*${FIS_AMOUNT_SOURCE}\s*$`, 'i').test(clean)) return true;
  return false;
}

function looksLikeReceiptProductName(name = '') {
  const clean = cleanReceiptName(name);
  if (clean.length < 2 || clean.length > 90) return false;
  if (/^[\d\s.,*%₺+-]+$/.test(clean)) return false;
  if (BARCODE_RE.test(clean)) return false;
  if (FIS_SKIP.some((p) => p.test(clean))) return false;
  if (/\b(?:tl|try)\b/i.test(clean) && !/[a-zçğıöşü]{3,}/i.test(clean.replace(/\btl|try\b/gi, ''))) return false;
  if (FIS_DATE_RE.test(clean) || FIS_TIME_RE.test(clean)) return false;
  if (QUANTITY_ONLY_RE.test(clean.toLocaleLowerCase('tr-TR'))) return false;
  return true;
}

function pushReceiptItem(items, name, price) {
  const ad = cleanReceiptName(name);
  if (!looksLikeReceiptProductName(ad)) return;
  const fiyat = parseReceiptPrice(price);
  if (fiyat == null) return;

  // Aynı ürün fişte birden fazla kez geçebilir. Harcamaya ürün bazlı aktarımda
  // tekrar eden satırları da koru; kullanıcı önizlemede istemediğini kaldırabilir.
  items.push({ ad, fiyat });
}

function extractReceiptPrice(line = '') {
  const clean = normalizeReceiptLine(line);
  const match = clean.match(FIS_PRICE_TOKEN_RE);
  if (!match) return null;
  const token = match[1];
  const fiyat = parseReceiptPrice(token);
  if (fiyat == null) return null;
  const idx = clean.lastIndexOf(token.trim());
  const cutIndex = idx > -1 ? idx : clean.search(FIS_PRICE_TOKEN_RE);
  return {
    fiyat,
    priceToken: token,
    namePart: clean.slice(0, cutIndex > -1 ? cutIndex : clean.length).trim(),
  };
}

function normalizeReceiptRows(rawText) {
  const baseLines = String(rawText || '')
    .split(/\r?\n/)
    .map(normalizeReceiptLine)
    .filter(Boolean);

  const rows = [];
  for (let i = 0; i < baseLines.length; i++) {
    const line = baseLines[i];
    rows.push(line);

    // Bazı OCR çıktılarında ürün, KDV oranı ve fiyat yan yana değil art arda ayrı satır gelir.
    // Ürün + %KDV + fiyat üçlüsünü ek bir sanal satır olarak birleştir.
    if (i + 2 < baseLines.length && looksLikeReceiptProductName(line) && /^%\s*\d{1,2}/.test(baseLines[i + 1]) && FIS_PRICE_ONLY_RE.test(baseLines[i + 2])) {
      rows.push(`${line} ${baseLines[i + 1]} ${baseLines[i + 2]}`);
    }

    // Ürün + fiyat iki satıra düşerse sanal satır oluştur.
    if (i + 1 < baseLines.length && looksLikeReceiptProductName(line) && FIS_PRICE_ONLY_RE.test(baseLines[i + 1])) {
      rows.push(`${line} ${baseLines[i + 1]}`);
    }
  }
  return rows;
}

export function marketFisiParse(rawText) {
  if (!rawText) return [];

  const lines = normalizeReceiptRows(rawText);
  const items = [];
  let pendingName = '';

  for (const rawLine of lines) {
    const line = normalizeReceiptLine(rawLine);
    if (shouldSkipWholeLine(line)) continue;

    const priceOnly = line.match(FIS_PRICE_ONLY_RE);
    if (priceOnly) {
      if (pendingName) {
        pushReceiptItem(items, pendingName, priceOnly[1]);
        pendingName = '';
      }
      continue;
    }

    const extracted = extractReceiptPrice(line);
    if (extracted) {
      if (FIS_SKIP.some((p) => p.test(line))) {
        pendingName = '';
        continue;
      }
      if (/\b(?:satis|satış|ziraat|banka|tek\s*pos|kredi\s*kart|nakit|toplam|topkdv|kdv)\b/i.test(line)) {
        pendingName = '';
        continue;
      }

      let namePart = cleanReceiptName(extracted.namePart);

      // Aynı satırda ürün adı yoksa bir önceki ürün adını fiyatla eşleştir.
      if (!namePart && pendingName) namePart = pendingName;

      // A101/ŞOK/BİM/Migros gibi fişlerde ürün adının içinde KDV oranı kalabilir.
      namePart = cleanReceiptName(namePart);
      pushReceiptItem(items, namePart, extracted.fiyat);
      pendingName = '';
      if (items.length >= 100) break;
      continue;
    }

    const candidate = cleanReceiptName(line);
    if (looksLikeReceiptProductName(candidate)) {
      // Market üst bilgisi gibi tek satırları değil, ürün olma ihtimali yüksek satırları beklet.
      if (!FIS_SKIP.some((p) => p.test(candidate)) && !/\b(?:satis|satış|ziraat|banka|tek\s*pos|kredi\s*kart|nakit|toplam|topkdv|kdv)\b/i.test(candidate)) {
        if (PRODUCT_HINTS.test(candidate) || candidate.split(/\s+/).length >= 2 || /[A-ZÇĞİÖŞÜ]{3,}/.test(candidate)) {
          pendingName = candidate;
        }
      }
    }
  }

  return items;
}
